import { Directionality } from '@angular/cdk/bidi';
import { registerLocaleData } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import localeAr from '@angular/common/locales/ar';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import localeJa from '@angular/common/locales/ja';
import localeKo from '@angular/common/locales/ko';
import localeNl from '@angular/common/locales/nl';
import localePl from '@angular/common/locales/pl';
import localePt from '@angular/common/locales/pt';
import localeRu from '@angular/common/locales/ru';
import localeSv from '@angular/common/locales/sv';
import localeTr from '@angular/common/locales/tr';
import localeUk from '@angular/common/locales/uk';
import localeZh from '@angular/common/locales/zh';
import {
  EnvironmentProviders,
  inject,
  InjectionToken,
  LOCALE_ID,
  provideAppInitializer,
  Provider,
} from '@angular/core';
import { provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { Observable, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { CULTURE_COOKIE_EXPIRY, Cultures, IStringMap } from '@ideals/types';
import { getDomain } from '@ideals/utils';

import { CustomDirectionality } from './custom-directionality';

export const LOCALIZATION_BASE_URL = new InjectionToken<string>('LOCALIZATION_BASE_URL', {
  factory: () => '/',
});

export const BASED_ON_BROWSER_LANGUAGE = new InjectionToken<boolean>('BASED_ON_BROWSER_LANGUAGE', {
  factory: () => false,
});

export class LocalizeTranslateLoader implements TranslateLoader {
  readonly #httpClient = inject(HttpClient);
  readonly #localizationBaseUrl = inject(LOCALIZATION_BASE_URL);

  getTranslation(lang: string): Observable<IStringMap> {
    return this.#httpClient.get<IStringMap>(`${this.#localizationBaseUrl}localization/${lang}.json`).pipe(catchError(() => of({})));
  }
}

export class LocaleId extends String {
  readonly #translateService = inject(TranslateService);

  toString(): string {
    return this.#translateService.currentLang();
  }

  valueOf(): string {
    return this.#translateService.currentLang();
  }
}

export function provideRootTranslate(): (Provider | EnvironmentProviders)[] {
  return [
    provideTranslateService({
      loader: { provide: TranslateLoader, useClass: LocalizeTranslateLoader },
    }),
    {
      provide: LOCALE_ID,
      useClass: LocaleId,
    },
    {
      provide: Directionality,
      useClass: CustomDirectionality,
    },
    provideAppInitializer(() => {
      const cookieService = inject(CookieService);
      const translateService = inject(TranslateService);
      const browserLanguage = translateService.getBrowserLang();
      const basedOnBrowserLanguage = inject(BASED_ON_BROWSER_LANGUAGE);
      const directionality = inject(Directionality) as CustomDirectionality;

      registerLocaleData(localeDe, Cultures.DE);
      registerLocaleData(localeEn, Cultures.US);
      registerLocaleData(localeEs, Cultures.ES);
      registerLocaleData(localeFr, Cultures.FR);
      registerLocaleData(localeIt, Cultures.IT);
      registerLocaleData(localeJa, Cultures.JP);
      registerLocaleData(localeKo, Cultures.KR);
      registerLocaleData(localeNl, Cultures.NL);
      registerLocaleData(localePl, Cultures.PL);
      registerLocaleData(localePt, Cultures.BR);
      registerLocaleData(localeRu, Cultures.RU);
      registerLocaleData(localeSv, Cultures.SE);
      registerLocaleData(localeTr, Cultures.TR);
      registerLocaleData(localeUk, Cultures.UA);
      registerLocaleData(localeZh, Cultures.CN);
      registerLocaleData(localeAr, Cultures.SA);

      const getDefaultLanguage = (): string => {
        let language = Cultures.US;

        if (browserLanguage) {
          Object.values(Cultures).forEach((lang) => {
            if (lang.includes(browserLanguage)) {
              language = lang;
            }
          });
        }

        if (basedOnBrowserLanguage) {
          cookieService.set('culture', language, new Date(CULTURE_COOKIE_EXPIRY), '/', getDomain());
        }

        return language;
      };

      translateService.onLangChange
        .subscribe(({ lang }) => {
          const [cultureId] = lang.split('-');

          directionality.setByCulture(cultureId);
        });

      const culture = cookieService.get('culture') || getDefaultLanguage();

      translateService.setFallbackLang(culture);

      return translateService.use(culture).pipe(take(1));
    }),
  ];
}
